import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import type { AuditScope, Project } from '@seo/types';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useToast, toastError } from '@/components/ui/toast';
import { useProjects } from '@/features/projects/use-projects';
import { useCreateAudit } from './use-create-audit';

/**
 * v2 create-audit dialog. Lets the user pick a project and tune the crawl scope
 * (max_pages, max_depth, render_js, patterns, HTTPS/robots options).
 */
export function CreateAuditDialog({
  open,
  onClose,
  defaultProjectId,
}: {
  open: boolean;
  onClose: () => void;
  defaultProjectId?: string;
}): JSX.Element | null {
  const projects = useProjects();
  const create = useCreateAudit();
  const nav = useNavigate();
  const { toast } = useToast();

  const [projectId, setProjectId] = useState(defaultProjectId ?? '');
  const [maxPages, setMaxPages] = useState(200);
  const [maxDepth, setMaxDepth] = useState(5);
  const [renderJs, setRenderJs] = useState<'auto' | 'always' | 'never'>('auto');
  const [psiSampleSize, setPsiSampleSize] = useState(5);
  const [includePatterns, setIncludePatterns] = useState('');
  const [excludePatterns, setExcludePatterns] = useState('');
  const [followSubdomains, setFollowSubdomains] = useState(false);
  const [respectRobots, setRespectRobots] = useState(true);

  if (!open) return null;

  const submit = async (): Promise<void> => {
    if (!projectId) {
      toast({ title: 'Pick a project' });
      return;
    }
    try {
      const scope: Partial<AuditScope> = {
        max_pages: maxPages,
        max_depth: maxDepth,
        render_js: renderJs,
        psi_sample_size: psiSampleSize,
        follow_subdomains: followSubdomains,
        respect_robots: respectRobots,
        include_patterns: parsePatterns(includePatterns),
        exclude_patterns: parsePatterns(excludePatterns),
      };
      const audit = await create.mutateAsync({ project_id: projectId, scope });
      toast({ title: 'Audit started' });
      onClose();
      void nav({ to: '/audits/$auditId', params: { auditId: audit.id } });
    } catch (e) {
      toastError(toast, e);
    }
  };

  return (
    <div
      className="fixed inset-0 z-40 flex items-center justify-center bg-black/70"
      role="dialog"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="w-full max-w-2xl overflow-hidden rounded-lg border border-border/60 bg-background p-6 shadow-xl">
        <h2 className="mb-1 text-xl font-semibold">New audit</h2>
        <p className="mb-4 text-sm text-muted-foreground">
          Up to {maxPages} pages · {maxDepth} clicks deep ·{' '}
          {renderJs === 'always'
            ? 'JavaScript rendered on every page'
            : renderJs === 'never'
              ? 'JavaScript never rendered'
              : 'JavaScript rendered when needed'}
        </p>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Project">
            <select
              value={projectId}
              onChange={(e) => setProjectId(e.target.value)}
              className="h-9 w-full rounded-md border border-input bg-transparent px-2 text-sm"
            >
              <option value="">— select —</option>
              {(projects.data?.items ?? []).map((p: Project) => (
                <option key={p.id} value={p.id}>
                  {p.name} · {p.domain.replace(/^https?:\/\//, '')}
                </option>
              ))}
            </select>
          </Field>
          <Field label={`Max pages: ${maxPages}`}>
            <input
              type="range"
              min={10}
              max={1000}
              step={10}
              value={maxPages}
              onChange={(e) => setMaxPages(Number(e.target.value))}
              className="w-full"
            />
            <p className="mt-1 text-[11px] text-muted-foreground">Default 200 · UI max 1000</p>
          </Field>
          <Field label={`Max depth: ${maxDepth}`}>
            <input
              type="range"
              min={1}
              max={8}
              step={1}
              value={maxDepth}
              onChange={(e) => setMaxDepth(Number(e.target.value))}
              className="w-full"
            />
            <p className="mt-1 text-[11px] text-muted-foreground">Default 5 clicks deep</p>
          </Field>
          <Field label="JS rendering">
            <select
              value={renderJs}
              onChange={(e) => setRenderJs(e.target.value as 'auto' | 'always' | 'never')}
              className="h-9 w-full rounded-md border border-input bg-transparent px-2 text-sm"
            >
              <option value="auto">auto (recommended)</option>
              <option value="always">always render</option>
              <option value="never">never render</option>
            </select>
          </Field>
          <Field label={`PSI sample: ${psiSampleSize}`}>
            <input
              type="range"
              min={0}
              max={30}
              step={1}
              value={psiSampleSize}
              onChange={(e) => setPsiSampleSize(Number(e.target.value))}
              className="w-full"
            />
          </Field>
          <div className="grid grid-cols-2 items-center gap-2">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={followSubdomains}
                onChange={(e) => setFollowSubdomains(e.target.checked)}
              />
              Follow subdomains
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={respectRobots}
                onChange={(e) => setRespectRobots(e.target.checked)}
              />
              Respect robots.txt
            </label>
          </div>
          <Field label="Include URL patterns (one per line)" full>
            <textarea
              value={includePatterns}
              onChange={(e) => setIncludePatterns(e.target.value)}
              placeholder="https://example.com/blog/*"
              className="min-h-[64px] w-full rounded-md border border-input bg-transparent p-2 text-sm"
            />
          </Field>
          <Field label="Exclude URL patterns (one per line)" full>
            <textarea
              value={excludePatterns}
              onChange={(e) => setExcludePatterns(e.target.value)}
              placeholder="*/tag/*"
              className="min-h-[64px] w-full rounded-md border border-input bg-transparent p-2 text-sm"
            />
          </Field>
        </div>
        <div className="mt-6 flex items-center justify-end gap-2">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={create.isPending}>
            {create.isPending ? 'Starting…' : 'Start audit'}
          </Button>
        </div>
      </div>
    </div>
  );
}

function Field({
  label,
  children,
  full,
}: {
  label: string;
  children: React.ReactNode;
  full?: boolean;
}): JSX.Element {
  return (
    <div className={full ? 'col-span-2' : ''}>
      <label className="mb-1 block text-xs uppercase tracking-wider text-muted-foreground">
        {label}
      </label>
      {children}
    </div>
  );
}

function parsePatterns(text: string): string[] | undefined {
  const lines = text
    .split('\n')
    .map((l) => l.trim())
    .filter(Boolean);
  return lines.length > 0 ? lines : undefined;
}

// Suppress unused-var since it's kept for a future component wrapper if needed.
export const _unused = Input;
